import { Stack, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { deleteRepoIntegration, getProjectRepos, ProjectRepo, qk } from 'api'
import { Dialog, DialogContent, ToastContent } from 'elements/feedback'
import { Button } from 'elements/inputs'
import { useBoolean } from 'lib/hooks'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trans } from 'react-i18next'
import { toast } from 'react-toastify'

import { EditGitIntegration } from '../edit-git-integration'
import { IntegrationItem } from '../integration-item'
import { IntegrationsBlock } from '../integrations-block'
import { CreateGitHubIntegrationForm } from './create-github-integration-form'

type GitHubIntegrationsProps = {
  projectId: string
}

export const GitHubIntegrations = ({ projectId }: GitHubIntegrationsProps) => {
  const { t } = useTranslation()

  const $repos = useQuery(
    qk.projects.reposList.toKeyWithArgs({ projectId }),
    () => getProjectRepos({ projectId }),
  )

  const $deleteGitHubIntegration = useMutation(deleteRepoIntegration)

  const [selectedIntegration, setSelectedIntegration] =
    useState<ProjectRepo | null>(null)

  const createIntegrationDialogOpen = useBoolean()

  const [integrationToDelete, setIntegrationToDelete] =
    useState<ProjectRepo | null>(null)

  const closeDetailsDialog = () => {
    setSelectedIntegration(null)
  }

  const closeDeleteIntegrationDialog = () => {
    setIntegrationToDelete(null)
  }

  if ($repos.isLoading || $repos.isError) return null

  const gitHubRepos = $repos.data.filter(repo => repo.type === 'github')

  return (
    <>
      <IntegrationsBlock
        variant="GITHUB"
        connectButton={
          <Button
            variant="text"
            sx={{ color: 'primary.main' }}
            onClick={createIntegrationDialogOpen.setTrue}
          >
            {gitHubRepos.length > 0
              ? t('projects.settings.integrations.connect_new_integration')
              : t('common.connect')}
          </Button>
        }
        connectedIntegrations={
          gitHubRepos.length > 0 ? (
            <Stack spacing={2.5}>
              {gitHubRepos.map(repo => (
                <IntegrationItem
                  key={repo.repoId}
                  title={repo.repo}
                  subtitle={repo.baseBranch}
                  onEdit={() => setSelectedIntegration(repo)}
                  onDelete={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    setIntegrationToDelete(repo)
                  }}
                />
              ))}
            </Stack>
          ) : undefined
        }
      />

      <Dialog open={selectedIntegration !== null} onClose={closeDetailsDialog}>
        {selectedIntegration !== null && (
          <EditGitIntegration
            type="GITHUB"
            projectId={projectId}
            projectRepo={selectedIntegration}
            onClose={closeDetailsDialog}
          />
        )}
      </Dialog>

      <Dialog
        open={createIntegrationDialogOpen.isTrue}
        onClose={createIntegrationDialogOpen.setFalse}
      >
        <CreateGitHubIntegrationForm
          projectId={projectId}
          closeDialog={createIntegrationDialogOpen.setFalse}
        />
      </Dialog>

      <Dialog
        open={integrationToDelete !== null}
        onClose={closeDeleteIntegrationDialog}
      >
        {integrationToDelete !== null && (
          <DialogContent
            variant="danger"
            title={t('projects.settings.integrations.remove_integration')}
            isActionLoading={$deleteGitHubIntegration.isLoading}
            onConfirm={() => {
              $deleteGitHubIntegration.mutate(
                { projectId, repoId: integrationToDelete.repoId },
                {
                  onSuccess: () => {
                    closeDeleteIntegrationDialog()
                    $repos.refetch()
                    toast.success(
                      <ToastContent
                        title={t(
                          'projects.settings.integrations.github_integration_successfully_deleted',
                          { repoName: integrationToDelete.repo },
                        )}
                      />,
                    )
                  },
                },
              )
            }}
            onDeny={closeDeleteIntegrationDialog}
            confirmText={t('common.remove')}
            denyText={t('common.cancel')}
          >
            <Typography variant="caption1" color="text.secondary">
              <Trans
                i18nKey="projects.settings.integrations.delete_integration_confirmation"
                values={{
                  integrationName: integrationToDelete.repo,
                }}
              />
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}
